import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { signOut } from "firebase/auth";

import axiosInstance, { setAuthToken } from "../redux/network/https";
import { persistor, store } from "../redux/store";
import { logout } from "../redux/reducers/authReducers/authReducers";
import { endPoints } from "../redux/network/constant";
import { auth } from "../lib/firebase";
import { format } from "date-fns";

export const useIsAuthenticatedUser = () => {
  const user = useSelector((state) => state?.user?.user);
  const token = user?.token?.access;

  useEffect(() => {
    setAuthToken(token || null);
  }, [token]);

  if (!token) {
    return { isAuthenticated: false, isResetPassword: user?.is_reset_password };
  }
  return { isAuthenticated: true, isResetPassword: user?.is_reset_password };
};

// Logout Function
export const logoutFn = async () => {
  const refreshToken = store.getState()?.user?.user?.token?.refresh;
  if (refreshToken) {
    try {
      await axiosInstance.post(endPoints.Logout, { refresh: refreshToken });
    } catch (_) {
      // Best-effort — continue with local cleanup
    }
  }

  store.dispatch(logout());
  persistor.purge();
  setAuthToken(null);

  if (auth) signOut(auth);
};

// Debounce
export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Capitalize First Letter
export const capitalize = (string) => {
  if (!string?.trim()) return "";
  string = string?.toLowerCase();
  const capitalized = string
    ?.split(" ")
    ?.map((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1));
  return capitalized?.join(" ");
};

export const formatDate = (date, dateFormat = "MMM d, y") => {
  if (!date) return "";
  return format(date, dateFormat);
};

export const toIsoDate = (value) => {
  if (!value) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, "0");
    const day = `${value.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
    const [month, day, year] = trimmed.split("/");
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return "";
  const year = parsed.getFullYear();
  const month = `${parsed.getMonth() + 1}`.padStart(2, "0");
  const day = `${parsed.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Parse US date string (MM/DD/YYYY) to Date object
export const parseUsDate = (dateStr) => {
  if (!dateStr) return null;
  const [m, d, y] = dateStr.split("/");
  return new Date(Number(y), Number(m) - 1, Number(d));
};

export const toUsDate = (value) => {
  if (!value) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const month = `${value.getMonth() + 1}`.padStart(2, "0");
    const day = `${value.getDate()}`.padStart(2, "0");
    const year = value.getFullYear();
    return `${month}/${day}/${year}`;
  }
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
    const [month, day, year] = trimmed.split("/");
    return `${month.padStart(2, "0")}/${day.padStart(2, "0")}/${year}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [year, month, day] = trimmed.split("-");
    return `${month}/${day}/${year}`;
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return "";
  const month = `${parsed.getMonth() + 1}`.padStart(2, "0");
  const day = `${parsed.getDate()}`.padStart(2, "0");
  const year = parsed.getFullYear();
  return `${month}/${day}/${year}`;
};

// Check Active Routes
export const checkRoute = (route) => {
  return window?.location.pathname?.split("/")[1] === route?.split("/")[1];
};

export function formatToReadableTime(dateString) {
  const date = new Date(dateString);

  // Extract hours and minutes
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const amPm = hours >= 12 ? "PM" : "AM";

  // Convert 24-hour format to 12-hour format
  hours = hours % 12 || 12;

  // Format the time
  const formattedTime = `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")} ${amPm}`;

  return formattedTime;
}

export function formatDateLabel(dateString) {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const daysAgo = (today - date) / 1000 / 60 / 60 / 24;

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  } else if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  } else if (daysAgo < 7) {
    return date.toLocaleDateString("en-US", { weekday: "long" });
  } else {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
}

// Extract a meaningful error message from various backend error shapes
export const extractBackendErrorMessage = (
  err,
  fallback = "Something went wrong",
) => {
  if (!err) return fallback;
  // If already a string, return as-is
  if (typeof err === "string") return err;
  const data = err?.response?.data ?? err;

  // Direct string message at root
  if (typeof data === "string") return data;

  // Common fields
  if (typeof data?.message === "string") return data.message;
  if (Array.isArray(data?.message)) {
    for (const item of data.message) {
      if (!item) continue;
      if (typeof item === "string") return item;
      if (item?.non_field_errors && typeof item.non_field_errors === "string")
        return item.non_field_errors;
      const firstVal = Object.values(item || {}).find(
        (v) => typeof v === "string" && v.length,
      );
      if (firstVal) return firstVal;
    }
  }

  if (typeof data?.detail === "string") return data.detail;
  if (typeof data?.error === "string") return data.error;

  // Array at root (e.g., [{ non_field_errors: "..." }])
  if (Array.isArray(data)) {
    for (const item of data) {
      if (!item) continue;
      if (typeof item === "string") return item;
      if (item?.non_field_errors && typeof item.non_field_errors === "string")
        return item.non_field_errors;
      const firstVal = Object.values(item || {}).find(
        (v) => typeof v === "string" && v.length,
      );
      if (firstVal) return firstVal;
    }
  }

  // errors array
  if (Array.isArray(data?.errors)) {
    const first = data.errors.find((v) => v);
    if (typeof first === "string") return first;
    if (first?.message) return first.message;
  }

  // Fallbacks
  if (typeof err?.message === "string") return err.message;
  const firstString = Object.values(data || {}).find(
    (v) => typeof v === "string" && v.length,
  );
  if (firstString) return firstString;

  return fallback;
};

// Extract field-specific errors from backend validation errors
// Returns { fieldErrors: { fieldName: "error message" }, generalError: "message" }
export const extractBackendFieldErrors = (err, fieldMap = {}) => {
  const result = { fieldErrors: {}, generalError: null };
  if (!err) return result;

  const data = err?.response?.data ?? err;
  if (!data) return result;

  // Handle {message: [{field_name: "error"}, ...]} format
  if (Array.isArray(data?.message)) {
    for (const item of data.message) {
      if (!item || typeof item !== "object") continue;
      for (const [fieldName, errorMsg] of Object.entries(item)) {
        if (typeof errorMsg === "string" && errorMsg.length) {
          // Map backend field name to frontend field name if provided
          const mappedField = fieldMap[fieldName] || fieldName;
          result.fieldErrors[mappedField] = errorMsg;
        }
      }
    }
    // If no field errors were extracted, treat as general error
    if (Object.keys(result.fieldErrors).length === 0) {
      result.generalError = extractBackendErrorMessage(err);
    }
    return result;
  }

  // Handle {field_name: "error", ...} format
  if (typeof data === "object") {
    for (const [fieldName, errorMsg] of Object.entries(data)) {
      if (["status", "success", "message", "detail", "error"].includes(fieldName)) continue;
      if (typeof errorMsg === "string" && errorMsg.length) {
        const mappedField = fieldMap[fieldName] || fieldName;
        result.fieldErrors[mappedField] = errorMsg;
      } else if (Array.isArray(errorMsg) && errorMsg.length > 0) {
        const mappedField = fieldMap[fieldName] || fieldName;
        result.fieldErrors[mappedField] = errorMsg[0];
      }
    }
    if (Object.keys(result.fieldErrors).length > 0) return result;
  }

  // Fall back to general error message
  result.generalError = extractBackendErrorMessage(err);
  return result;
};

export const formatAddress = (mailingAddress, operatedAddress) => {
  // Check if mailingAddress has actual meaningful data - prioritize mailing location
  const hasMailingData =
    mailingAddress &&
    (mailingAddress.physical_address ||
      mailingAddress.address ||
      mailingAddress.city?.name ||
      mailingAddress.state?.name ||
      mailingAddress.zipcode);

  const address = hasMailingData ? mailingAddress : operatedAddress;

  if (!address) return "";

  if (address.physical_address) {
    return address.physical_address;
  }

  const city = address.city?.name || "";
  const state = address.state?.name || "";
  const zipcode = address.zipcode || "";

  return [city, state, zipcode].filter(Boolean).join(", ");
};

export const stripHtmlTags = (html) => {
  if (!html) return "";
  const temp = document.createElement("div");
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || "";
};

export const getFullName = (user) =>
  user?.full_name ||
  [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
  "";

export const getPartnerLocationLabel = (user) =>
  formatAddress(user?.mailing_address, user?.operated_address) ||
  user?.city_name ||
  "";
